import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Search, BookOpen, Calculator, Calendar, AlertCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";

/**
 * Global Search Bar Component
 * Search across modules, emissions, and periods
 */
export default function SearchBar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [, setLocation] = useLocation();

  // Debounce search query
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Search query
  const { data: results = [], isLoading, error } = trpc.search.query.useQuery(
    { q: debouncedQuery, limit: 10 },
    { enabled: debouncedQuery.length > 0 }
  );

  const handleSelect = (result: any) => {
    if (result.type === "module") {
      setLocation(`/learn/${result.id}`);
    } else if (result.type === "emission") {
      setLocation(`/calculator`);
    } else if (result.type === "period") {
      setLocation(`/calculator`);
    }
    setOpen(false);
    setQuery("");
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "module":
        return <BookOpen className="h-4 w-4" />;
      case "emission":
        return <Calculator className="h-4 w-4" />;
      case "period":
        return <Calendar className="h-4 w-4" />;
      default:
        return <Search className="h-4 w-4" />;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-start text-muted-foreground"
        >
          <Search className="mr-2 h-4 w-4" />
          {t('common.search')}...
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t('search.placeholder')}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {isLoading && (
              <div className="py-6 text-center text-sm">{t('search.searching')}</div>
            )}
            {error && (
              <div className="py-6 px-4 text-center text-sm text-destructive flex items-center justify-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>{t('search.error')}</span>
              </div>
            )}
            {!isLoading && !error && query.length > 0 && results.length === 0 && (
              <CommandEmpty>{t('search.no_results')}</CommandEmpty>
            )}
            {!isLoading && results.length > 0 && (
              <CommandGroup heading={t('search.results')}>
                {results.map((result: any) => (
                  <CommandItem
                    key={`${result.type}-${result.id}`}
                    onSelect={() => handleSelect(result)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start gap-2 w-full">
                      {getIcon(result.type)}
                      <div className="flex-1">
                        <div className="font-medium">{result.title}</div>
                        {result.description && (
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {result.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

